/**
 * Service Worker Update Hook
 *
 * Detects when a new service worker is available and provides
 * functions to prompt the user to update.
 */

import { useEffect, useState, useCallback, useRef } from 'react';

export type UpdateCheckResult = 'update-available' | 'up-to-date' | 'unsupported' | 'failed';

interface ServiceWorkerState {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker: () => void;
}

/**
 * Ask the registration to check for an update.
 *
 * Mobile Safari can reject with `InvalidStateError: newestWorker is null`
 * when the registration isn't ready for an update check yet (e.g. right as
 * the tab becomes visible again). That must not become an unhandled rejection.
 */
export function checkServiceWorkerUpdate(
  registration: Pick<ServiceWorkerRegistration, 'update'>
): Promise<void> {
  return registration.update().then(
    () => undefined,
    (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      if (/newestWorker is null/i.test(message) || (error as { name?: string })?.name === 'InvalidStateError') {
        return;
      }
      console.debug('SW update check skipped:', error);
    }
  );
}

/**
 * Check for an update without owning a registration.
 *
 * `useServiceWorker` registers the worker, polls, listens for controller
 * changes and reloads. A second caller that only wants to ask "is there an
 * update?" does not want any of that — mounting the hook twice gave the app two
 * registrations, two polls, two visibility handlers and two reload listeners,
 * each with its own guard, which is how a single-reload fix turns back into a
 * double reload.
 */
/**
 * Whether it is safe to reload for a new service worker right now.
 *
 * Workers activate eagerly, so `controllerchange` can fire at any moment
 * rather than only when someone taps "update". Reloading a judge mid-match
 * would drop a selected winner and interrupt scoring at a table with players
 * waiting — worse than running a slightly old build for a few more minutes.
 *
 * Skipping is safe rather than merely deferring: the new worker is already
 * active and controlling, so the next real navigation serves the new assets
 * without any further prompting.
 */
export function shouldReloadForUpdate(pathname: string): boolean {
  return !/\/match\/[^/]+\/ref$/.test(pathname);
}

export async function checkForUpdateNow(): Promise<UpdateCheckResult> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return 'unsupported';

  try {
    await checkServiceWorkerUpdate(registration);
  } catch {
    return 'failed';
  }

  // update() resolves when the check is done, but a new worker still has to
  // install before it reaches `waiting`. Reporting "up to date" while the
  // answer is still arriving is the one wrong thing this can say.
  for (let i = 0; i < 10; i++) {
    if (registration.waiting || registration.installing) break;
    await new Promise((resolve) => setTimeout(resolve, 300));
  }
  if (registration.installing) {
    await new Promise<void>((resolve) => {
      const worker = registration.installing;
      if (!worker) return resolve();
      const onChange = () => {
        if (worker.state === 'installed' || worker.state === 'activated') {
          worker.removeEventListener('statechange', onChange);
          resolve();
        }
      };
      worker.addEventListener('statechange', onChange);
      setTimeout(resolve, 5000);
    });
  }
  return registration.waiting ? 'update-available' : 'up-to-date';
}

/** Activate a waiting worker. The reload follows from `controllerchange`. */
export async function applyWaitingUpdate(): Promise<void> {
  const registration = await navigator.serviceWorker?.getRegistration();
  registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

export function useServiceWorker(): ServiceWorkerState {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const updateServiceWorker = useCallback(() => {
    if (waitingWorker) {
      // Ask the waiting worker to activate. The single reload happens in the
      // controllerchange handler once it takes control — reloading here too
      // caused a jarring double reload.
      setNeedRefresh(false);
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [waitingWorker]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    let reloading = false;
    let updateInterval: ReturnType<typeof setInterval> | undefined;
    const handleControllerChange = () => {
      // The new worker took control — reload once to pick it up, unless doing
      // so would interrupt a match. See shouldReloadForUpdate.
      if (reloading) return;
      if (!shouldReloadForUpdate(window.location.pathname)) return;
      reloading = true;
      window.location.reload();
    };

    const onVisibilityChange = (registration: ServiceWorkerRegistration) => {
      if (document.visibilityState === 'visible') {
        void checkServiceWorkerUpdate(registration);
      }
    };

    let visibilityHandler: (() => void) | undefined;

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          type: 'module',
        });

        console.log('SW registered:', registration.scope);
        registrationRef.current = registration;

        // Check if there's already a waiting worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setNeedRefresh(true);
        }

        // Listen for new service workers
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is installed but waiting
              setWaitingWorker(newWorker);
              setNeedRefresh(true);
            } else if (newWorker.state === 'activated') {
              // Service worker is ready for offline use
              setOfflineReady(true);
            }
          });
        });

        // Check for updates periodically (every 60 seconds)
        updateInterval = setInterval(() => {
          void checkServiceWorkerUpdate(registration);
        }, 60 * 1000);

        // Also check for updates on visibility change (when user returns to tab)
        visibilityHandler = () => onVisibilityChange(registration);
        document.addEventListener('visibilitychange', visibilityHandler);

      } catch (error) {
        console.error('SW registration failed:', error);
      }
    };

    // Listen for controller changes
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Register on load
    if (document.readyState === 'complete') {
      void registerSW();
    } else {
      window.addEventListener('load', registerSW);
    }

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      window.removeEventListener('load', registerSW);
      if (updateInterval) clearInterval(updateInterval);
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
    };
  }, []);

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
  };
}
